import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Newspaper } from "lucide-react";
import { api } from "@/lib/api";
import { useReveal } from "@/hooks/useReveal";

export const fmtDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
export const excerpt = (t, n = 160) => (t.length > n ? t.slice(0, n).replace(/\s+\S*$/, "") + "…" : t);

export function News() {
  const head = useReveal();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/news", { params: { limit: 3 } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  if (items && items.length === 0) return null;

  return (
    <section id="actus" data-testid="news-section" className="relative py-20 md:py-28 bg-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div ref={head.ref} className={`flex flex-wrap items-end justify-between gap-6 ${head.className}`}>
          <div>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-emerald-bright mb-3">La vie du club</p>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">
              Dernières <span className="text-mustard">actus</span>
            </h2>
          </div>
          <Link to="/actus" data-testid="news-all-link" className="inline-flex items-center gap-2 text-sm font-sub font-semibold text-cream link-underline">
            Toutes les actus <ArrowUpRight className="w-4 h-4 text-mustard" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-12" data-testid="news-grid">
          {(items || [1, 2, 3]).map((n, i) => (
            items ? <NewsCard key={n.id} item={n} i={i} /> : <div key={i} className="h-80 rounded-3xl bg-ink-2 animate-pulse" />
          ))}
        </div>
      </div>
    </section>
  );
}

export function NewsCard({ item, i }) {
  return (
    <Link to={`/actus/${item.slug || item.id}`} data-testid={`news-card-${i}`}
      className="group relative rounded-3xl overflow-hidden border border-white/5 bg-ink-2 flex flex-col card-hover">
      <div className="relative aspect-[16/10] overflow-hidden bg-ink">
        {item.image_url ? (
          <img src={item.image_url} alt="" className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[radial-gradient(circle_at_30%_30%,rgba(221,55,51,0.25),transparent_60%)]">
            <Newspaper className="w-12 h-12 text-mustard/60" />
          </div>
        )}
        <span className="absolute top-4 left-4 rounded-md px-2 py-0.5 text-[10px] font-mono2 uppercase tracking-wider bg-ink/80 text-cream/80 border border-white/10">{fmtDate(item.created_at)}</span>
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="font-display font-bold text-lg md:text-xl tracking-tight text-cream group-hover:text-mustard transition-colors">{item.subject}</h3>
        <p className="mt-3 text-sm text-cream/60 leading-relaxed flex-1">{excerpt(item.content)}</p>
        <span className="mt-5 inline-flex items-center gap-2 text-sm font-sub font-semibold text-cream/80">Lire <ArrowUpRight className="w-4 h-4 text-mustard" /></span>
      </div>
    </Link>
  );
}
